import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import { IconButton, Popover, TextField, Tooltip, Box, Typography } from "@mui/material";

import { ChatBubble, ChatBubbleOutline } from "@mui/icons-material";

import { useDispatch } from "react-redux";
import { colors } from "../../../../../../../../../ui/colors";
import { quoteCreateActions } from "../../../../../../../../../../redux/features/quotes/quote-create-advanced";

interface TaskNoteBubbleProps {
  areaUid: string;
  taskId: string;
  taskName: string;
  noteFromStore?: string;
  icon?: ReactNode;
}

export const TaskNoteBubble = ({ areaUid, taskId, taskName, noteFromStore = "", icon }: TaskNoteBubbleProps) => {
  const dispatch = useDispatch();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const MAX_LENGTH = 100;

  const noteRef = useRef(noteFromStore || "");
  const [length, setLength] = useState(noteRef.current.length);

  useEffect(() => {
    noteRef.current = noteFromStore || "";
    setLength(noteRef.current.length);
  }, [noteFromStore]);

  const open = Boolean(anchorEl);
  const id = open ? `quote-note-popover-${taskId}` : undefined;

  const hasNote = String(noteFromStore || "").trim().length > 0;

  const saveNote = () => {
    const note = noteRef.current.slice(0, MAX_LENGTH).trim();

    dispatch(
      quoteCreateActions.updateTaskNote({
        areaUid,
        taskId,
        note,
      }),
    );
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (event?: any) => {
    event?.stopPropagation?.();

    saveNote();
    setAnchorEl(null);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value.slice(0, MAX_LENGTH);

    noteRef.current = value;
    setLength(value.length);
  };

  const inputKey = useMemo(() => `${taskId}:${open ? "open" : "closed"}:${noteFromStore}`, [taskId, open, noteFromStore]);

  return (
    <>
      <Tooltip title={hasNote ? "Edit note" : "Add note"}>
        <IconButton
          size="small"
          onClick={handleOpen}
          sx={{
            color: hasNote ? colors.orange.base : colors.gray[300],
            "&:hover": {
              color: colors.orange[500],
            },
            mr: 0.5,
            zIndex: 9999,
            position: "relative",
          }}
        >
          {icon ?? (hasNote ? <ChatBubble fontSize="small" /> : <ChatBubbleOutline fontSize="small" />)}
        </IconButton>
      </Tooltip>

      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        PaperProps={{
          sx: {
            p: 1.5,
            width: 260,
            borderRadius: 2,
            zIndex: 99999,
          },
        }}
        sx={{ zIndex: 99999 }}
      >
        <Box onClick={(event) => event.stopPropagation()}>
          <TextField
            key={inputKey}
            multiline
            rows={3}
            fullWidth
            placeholder={`Write a note for task: ${taskName || "Service"}...`}
            defaultValue={noteFromStore || ""}
            onChange={handleChange}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                event.preventDefault();
                event.stopPropagation();
                handleClose(event);
              }

              if (event.key === "Escape") {
                event.preventDefault();
                event.stopPropagation();
                setAnchorEl(null);
              }
            }}
            size="small"
            inputProps={{
              maxLength: MAX_LENGTH,
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: colors.gray[200],
                },
                "&:hover fieldset": {
                  borderColor: colors.orange[400],
                },
                "&.Mui-focused fieldset": {
                  borderColor: colors.orange.base,
                },
              },
              "& .MuiInputBase-input": {
                fontSize: 13,
              },
            }}
          />

          <Typography
            variant="caption"
            sx={{
              display: "block",
              textAlign: "right",
              mt: 0.5,
              color: length >= MAX_LENGTH ? colors.orange.base : colors.gray[400],
            }}
          >
            {length}/{MAX_LENGTH}
          </Typography>
        </Box>
      </Popover>
    </>
  );
};
